import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from '../../routing';
import { commandCenterApi, formatCurrency } from '../../utils/api';
import { useIsMobile } from '../../utils/useIsMobile';
import { formatDateShort } from '../../utils/dateFormat';
import Icon from '../../components/Icon';
import MultiSelectFilter from '../../components/MultiSelectFilter';
import { toast } from '../../components/Toast';
import {
  ADJUSTMENT_TYPES, ADJUSTMENT_TYPE_LABELS, LINE_KIND_ADJUSTMENT, LINE_KINDS, LINE_KIND_LABELS, lineKindLabel,
  patchSummary,
} from '../../utils/lineKind';
import { LoadState, openInApp } from './parts';
import RulesView from './LineKindRulesView';

// Command Center → שורות מסמכים (#286). Find document lines across every
// restaurant — by name/SKU and/or supplier — and classify them: מוצר, שירות,
// or לא מוצר (a row that only moves the amount — הנחה, דמי משלוח, עיגול…,
// with that sub-type optional), so metrics can later leave the לא מוצר rows
// out (server/lib/lineKind.js). Admin only, like the whole console.
//
// Nothing is listed until there is a search of 2+ characters or a supplier:
// the server refuses an unfiltered list (a substring search walks the table).

const NO_IDS = Object.freeze([]);
// = MAX_CLASSIFY_IDS in server/lib/ccLineItems.js — the most one request may carry.
const CLASSIFY_BATCH = 500;
// = MAX_FILTER_IDS in server/lib/ccLineItems.js — supplier ids per request.
const MAX_FILTER_IDS = 200;
const PAGE_SIZE = 200;
const MIN_QUERY_CHARS = 2;
const SEARCH_DEBOUNCE_MS = 300;

const KINDS = [
  { key: 'all', label: 'הכל' },
  { key: 'unclassified', label: 'לא מסווג' },
  ...LINE_KINDS.map((k) => ({ key: k, label: LINE_KIND_LABELS[k] })),
];
const PILL_TONE = { product: 'ok', service: '', adjustment: 'warn' };

const splitList = (v) => (v || '').split(',').filter(Boolean);
// "שורה אחת" / "3 שורות" — Hebrew counts read wrong as "1 שורות".
// The number on a tab. A capped count (more matches than the server counts)
// can only say "N+" for הכל — the per-class split of a partial count would be
// wrong, so those tabs show no number then.
function tabCount(counts, key) {
  if (!counts) return null;
  if (!counts.capped) return (counts[key] ?? 0).toLocaleString('en-US');
  return key === 'all' ? `${counts.all.toLocaleString('en-US')}+` : null;
}

// Counts per tab from rows already on screen (kind=all, no further page).
function countRows(rows) {
  const c = { all: rows.length, unclassified: 0, product: 0, service: 0, adjustment: 0 };
  for (const r of rows) {
    const key = r.line_kind ? r.line_kind : 'unclassified';
    if (key in c) c[key] += 1;
  }
  return c;
}
const linesCount = (n) => (n === 1 ? 'שורה אחת' : `${n} שורות`);

// "בכל המערכת: 45,210 שורות · מוצר 120 · …" — every live line, all restaurants.
function SummaryLine({ summary }) {
  const n = (v) => (v ?? 0).toLocaleString('en-US');
  const done = summary.all ? Math.round(((summary.all - summary.unclassified) / summary.all) * 100) : 0;
  return (
    // A named group (a <p> isn't a naming role, so its aria-label was ignored).
    <div className="cc-lines-summary" role="group" aria-label="סיכום שורות בכל המערכת">
      <span>בכל המערכת: <strong>{n(summary.all)}</strong> {summary.all === 1 ? 'שורה' : 'שורות'}</span>
      {LINE_KINDS.map((k) => (
        <span key={k}><span aria-hidden="true">· </span>{LINE_KIND_LABELS[k]} <strong>{n(summary[k])}</strong></span>
      ))}
      <span><span aria-hidden="true">· </span>לא מסווג <strong>{n(summary.unclassified)}</strong></span>
      <span className="cc-muted">({done}% סווגו)</span>
    </div>
  );
}

function KindPill({ row }) {
  const label = lineKindLabel(row.line_kind, row.adjustment_type);
  if (!label) return <span className="cc-muted">לא מסווג</span>;
  return (
    <span className="cc-kind-cell">
      <span className={`cc-pill ${PILL_TONE[row.line_kind] || ''}`.trim()}>{label}</span>
      {/* Classified by a rule, not a person (#300) — a hand mark overrides it. */}
      {row.line_kind_source === 'rule' && <span className="cc-muted cc-rule-tag" title="סווג אוטומטית לפי כלל">אוטומטי</span>}
    </span>
  );
}

function DocLink({ row }) {
  return (
    <button
      type="button"
      className="cc-linkbtn"
      // preventDefault too: on a phone the link sits inside the row's <label>,
      // and a click there would also tick the row's checkbox.
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); openInApp(`/document/${row.document_id}`, row.restaurant_id); }}
    >
      {row.doc_type} {row.doc_num || ''}
    </button>
  );
}

function LinesTable({ rows, selected, onToggle, onToggleAll }) {
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someChecked = !allChecked && rows.some((r) => selected.has(r.id));
  const headRef = useRef(null);
  useEffect(() => { if (headRef.current) headRef.current.indeterminate = someChecked; }, [someChecked]);
  return (
    // No overflow wrapper: the console's sticky <th> (commandCenter.css) docks
    // to the page, and a scrolling ancestor would pin it to that box instead.
    <table className="cc-lines-table">
      <thead>
        <tr>
          <th className="cc-check-cell">
            <input
              ref={headRef}
              type="checkbox"
              checked={allChecked}
              onChange={onToggleAll}
              aria-label="בחר את כל השורות המוצגות"
            />
          </th>
          <th>שורה</th>
          <th>ספק</th>
          <th>מסעדה</th>
          <th>מסמך</th>
          <th>תאריך</th>
          <th className="cc-num">כמות</th>
          <th className="cc-num">מחיר</th>
          <th className="cc-num">סה״כ</th>
          <th>סיווג</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={row.id}
            className={`cc-line-row${selected.has(row.id) ? ' is-selected' : ''}`}
            onClick={() => onToggle(row.id)}
          >
            <td className="cc-check-cell">
              <input
                type="checkbox"
                checked={selected.has(row.id)}
                onClick={(e) => e.stopPropagation()}
                onChange={() => onToggle(row.id)}
                aria-label={`בחר: ${row.description}`}
              />
            </td>
            <td>
              {row.description}
              {row.sku ? <span className="cc-muted"> · {row.sku}</span> : null}
            </td>
            <td>{row.supplier_name || <span className="cc-muted">לא זוהה ספק</span>}</td>
            <td>{row.restaurant_name}</td>
            <td><DocLink row={row} /></td>
            <td className="cc-muted">{formatDateShort(row.date)}</td>
            <td className="cc-num">{row.qty}</td>
            <td className="cc-num">{formatCurrency(row.unit_price)}</td>
            <td className="cc-num">{formatCurrency(row.line_total)}</td>
            <td><KindPill row={row} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Below the table breakpoint: a two-line row whose whole surface is the
// checkbox's label. Not ListRow — that is a <button>, and a checkbox inside a
// button is invalid HTML.
function LinesList({ rows, selected, onToggle }) {
  return (
    <div className="m-list">
      {rows.map((row) => (
        <label key={row.id} className={`m-row cc-line-mrow${selected.has(row.id) ? ' is-selected' : ''}`}>
          <input
            type="checkbox"
            checked={selected.has(row.id)}
            onChange={() => onToggle(row.id)}
            aria-label={`בחר: ${row.description}`}
          />
          <div className="m-row-main">
            <div className="m-row-primary">{row.description}</div>
            <div className="m-row-meta">
              <span>{row.supplier_name || 'לא זוהה ספק'}</span>
              <span>·</span>
              <span>{row.restaurant_name}</span>
              <span>·</span>
              <span>{formatDateShort(row.date)}</span>
              <span>·</span>
              {/* The phone's only way to the source invoice before classifying
                  its lines (Codex review, PR #289) — same tenant-aware handoff
                  as the desktop table's document cell. */}
              <DocLink row={row} />
              <KindPill row={row} />
            </div>
          </div>
          <div className="m-row-end">
            <span className="m-row-amount">{formatCurrency(row.line_total)}</span>
          </div>
        </label>
      ))}
    </div>
  );
}

// Two views of one page (#300): the lines, and the rules that classify new
// lines automatically. Split at the top so the lines view's requests never run
// while the rules are open, and vice versa. ?view=rules rides the URL.
export default function LineItemsPage() {
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'rules' ? 'rules' : 'lines';
  const setView = (v) => {
    const next = new URLSearchParams(params);
    if (v === 'rules') next.set('view', 'rules'); else next.delete('view');
    setParams(next, { replace: true });
  };
  // The rule count on the switch: one light read on open, kept current by
  // the rules view while it's open.
  const [ruleCount, setRuleCount] = useState(null);
  useEffect(() => {
    let live = true;
    commandCenterApi.getLineKindRules()
      .then((r) => { if (live) setRuleCount(r?.data?.rows?.length ?? null); })
      .catch(() => { if (live) setRuleCount(null); });
    return () => { live = false; };
  }, []);
  const header = (
    <div className="cc-lines-head">
      <h1 className="cc-page-title">שורות מסמכים</h1>
      <div className="cc-view-switch" role="group" aria-label="תצוגה">
        <button type="button" className="cc-view-btn" aria-pressed={view === 'lines'} onClick={() => setView('lines')}>שורות</button>
        <button type="button" className="cc-view-btn" aria-pressed={view === 'rules'} onClick={() => setView('rules')}>
          כללי סיווג{ruleCount != null ? ` (${ruleCount})` : ''}
        </button>
      </div>
    </div>
  );
  return view === 'rules'
    ? <>{header}<RulesView onCount={setRuleCount} /></>
    : <LinesView header={header} />;
}

function LinesView({ header }) {
  const [params, setParams] = useSearchParams();
  const isMobile = useIsMobile();

  const q = (params.get('q') || '').trim();
  // Memoised on the raw param STRING, not on `params`: a tab switch rewrites
  // the URL, and a fresh (equal) array here re-ran the counts request.
  const supplierParam = params.get('supplier') || '';
  const restaurantParam = params.get('restaurant') || '';
  // Two supplier modes, never mixed:
  //   - picked:  ?supplier=<keys> — exactly the suppliers ticked;
  //   - all-but: ?supplier_all=1&supplier_ex=<keys> — entered ONLY by pressing
  //     בחר הכל, then unticking. It means every supplier, including documents
  //     with no supplier and suppliers added later, minus the excluded ones.
  // Ticking most suppliers one by one stays "picked": silently turning it into
  // "all but" pulled in lines nobody ticked (internal review, PR #289).
  // supplier_all wins over a hand-edited supplier= in the same URL.
  const allSuppliers = params.get('supplier_all') === '1';
  const supplierKeys = useMemo(
    () => (allSuppliers ? NO_IDS : splitList(supplierParam)),
    [allSuppliers, supplierParam],
  );
  const excludedParam = params.get('supplier_ex') || '';
  const excludedKeys = useMemo(() => splitList(excludedParam), [excludedParam]);
  const restaurantIds = useMemo(() => splitList(restaurantParam), [restaurantParam]);
  const kind = KINDS.some((k) => k.key === params.get('kind')) ? params.get('kind') : 'all';

  const [qInput, setQInput] = useState(q);
  const [groups, setGroups] = useState([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [groupsFailed, setGroupsFailed] = useState(false);
  const [restaurants, setRestaurants] = useState([]);
  const [rows, setRows] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [adjMenuOpen, setAdjMenuOpen] = useState(false);   // the "לא מוצר ▾" sub-type menu
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);   // whole system by class; null = hidden
  const [counts, setCounts] = useState(null);   // per-tab line counts; null = none to show

  const updateParam = useCallback((key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  }, [params, setParams]);

  // Typing rides the URL after a short pause, so a pasted link reproduces the
  // search but every keystroke is not a request.
  useEffect(() => {
    const trimmed = qInput.trim();
    if (trimmed === q) return undefined;
    const t = setTimeout(() => updateParam('q', trimmed), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [qInput, q, updateParam]);

  useEffect(() => {
    commandCenterApi.getLineItemSuppliers()
      .then((r) => setGroups(Array.isArray(r.data?.rows) ? r.data.rows : []))
      .catch(() => { setGroups([]); setGroupsFailed(true); })
      .finally(() => setGroupsLoaded(true));
    commandCenterApi.getCustomers()
      .then((r) => setRestaurants((r.data || []).filter((c) => !c.is_demo)))
      .catch(() => setRestaurants([]));
  }, []);

  // The summary line: fetched once on open — never per search. A
  // classification patches it from the server's `moved` counts (classify).
  // A failure just hides it.
  // Every summary request gets a generation; only the newest may land. And a
  // classification can only PATCH a summary that has landed: one made while
  // the first summary was still loading used to patch null (lost), then the
  // late pre-classification snapshot appeared (Codex review, PR #294) — so
  // then it fetches a fresh summary instead, which also outdates the old one.
  const summaryGenRef = useRef(0);
  const summaryStateRef = useRef('pending');   // 'pending' | 'ok' | 'failed'
  const loadSummary = useCallback(() => {
    const gen = ++summaryGenRef.current;
    summaryStateRef.current = 'pending';
    commandCenterApi.getLineItemSummary()
      .then((r) => {
        if (gen !== summaryGenRef.current) return;
        setSummary(r.data || null);
        summaryStateRef.current = r.data ? 'ok' : 'failed';
      })
      .catch(() => {
        if (gen !== summaryGenRef.current) return;
        setSummary(null);
        summaryStateRef.current = 'failed';
      });
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const groupByKey = useMemo(() => new Map(groups.map((g) => [g.key, g])), [groups]);
  // With no supplier picked this must stay the SAME empty array when the
  // supplier options arrive — a fresh [] re-ran load() once and wiped any rows
  // already ticked.
  const supplierIds = useMemo(
    () => (supplierKeys.length ? supplierKeys.flatMap((k) => groupByKey.get(k)?.supplier_ids || []) : NO_IDS),
    [supplierKeys, groupByKey],
  );
  const restaurantName = useMemo(() => new Map(restaurants.map((r) => [String(r.id), r.name])), [restaurants]);
  // Only keys that still name a supplier group count: a stale key from an old
  // link is ignored rather than claimed in "הכל חוץ מ-N".
  const resolvedExcludedKeys = useMemo(
    () => (allSuppliers && excludedKeys.length ? excludedKeys.filter((k) => groupByKey.has(k)) : NO_IDS),
    [allSuppliers, excludedKeys, groupByKey],
  );
  const excludeSupplierIds = useMemo(
    () => (resolvedExcludedKeys.length
      ? resolvedExcludedKeys.flatMap((k) => groupByKey.get(k)?.supplier_ids || []) : NO_IDS),
    [resolvedExcludedKeys, groupByKey],
  );
  // Everything a request needs to know about the supplier filter.
  const supplierFilter = useMemo(
    () => ({ supplierIds, allSuppliers, excludeSupplierIds }),
    [supplierIds, allSuppliers, excludeSupplierIds],
  );
  // "All but X" can't be asked until X's supplier ids are known — asking early
  // would list X too, then reload.
  // If the supplier list failed to load, "all but X" can't name X — never
  // run it as plain "all" (that would show the lines the admin left out).
  // A supplier filter in the URL is only known once the supplier list has
  // loaded (keys → ids). Until then NOTHING loads — a search must not stand
  // in for it: with q typed, the list used to load with the supplier part
  // missing and showed the very suppliers the admin had left out (all-but) or
  // never picked, where they could be classified (Codex review, PR #294).
  const supplierFilterInUrl = supplierKeys.length > 0 || (allSuppliers && excludedKeys.length > 0);
  const supplierFilterPending = supplierFilterInUrl && !groupsLoaded;
  const supplierFilterBroken = supplierFilterInUrl && groupsLoaded
    && (groupsFailed || (supplierKeys.length > 0 && supplierIds.length === 0));
  const allReady = allSuppliers && !supplierFilterPending && !supplierFilterBroken;
  // A group expands to one supplier id per restaurant, so a selection of
  // fewer groups can still pass the server's id cap — say so instead of a 400.
  const tooManySuppliers = supplierIds.length > MAX_FILTER_IDS || excludeSupplierIds.length > MAX_FILTER_IDS;
  const ready = !tooManySuppliers && !supplierFilterPending && !supplierFilterBroken
    && (q.length >= MIN_QUERY_CHARS || supplierIds.length > 0 || allReady);
  let notReadyMessage = 'הקלידו לפחות 2 תווים או בחרו ספק כדי להציג שורות';
  if (tooManySuppliers) notReadyMessage = `בחירת הספקים ארוכה מדי (יותר מ-${MAX_FILTER_IDS} ספקים בכל המסעדות) — צמצמו אותה`;
  else if (supplierFilterPending) notReadyMessage = 'טוען את רשימת הספקים…';
  else if (supplierFilterBroken && groupsFailed) notReadyMessage = 'לא הצלחנו לטעון את רשימת הספקים — רעננו את הדף';
  else if (supplierFilterBroken) notReadyMessage = 'הספקים שבקישור כבר לא קיימים — בחרו ספק מחדש';

  const loadToken = useRef(0);
  // `before` = the last id on screen: keyset paging, so lines that arrive
  // between pages never repeat a row (server/lib/ccLineItems.js).
  const fetchPage = useCallback((before) => commandCenterApi.getLineItems({
    q: q.length >= MIN_QUERY_CHARS ? q : '', ...supplierFilter, restaurantIds, kind, limit: PAGE_SIZE, before,
  }), [q, supplierFilter, restaurantIds, kind]);

  // The tab counters (#286 perf review). They depend on the search /
  // supplier / restaurant filter only — never on the tab or on "טען עוד" — so:
  //   - kind=all with no further page: the rows on screen ARE every match, so
  //     the counts come from them and no request is made;
  //   - otherwise ONE counts request per filter, fired only after the list for
  //     that filter came back (a search already replaced by the next keystroke
  //     never costs a count — on a big table each is a full walk of items);
  //   - switching tabs reuses the counts already shown.
  // A failed count just hides the numbers; the list has its own error state.
  const filterKey = useMemo(
    () => JSON.stringify([q.length >= MIN_QUERY_CHARS ? q : '', supplierFilter, restaurantIds]),
    [q, supplierFilter, restaurantIds],
  );
  const countsKeyRef = useRef(null);   // the filterKey the counts on screen belong to
  // Every counts request gets a generation; only the newest may land. The
  // filter key alone can't tell them apart: a classification re-counts the
  // SAME filter, and the older reply arriving last used to put the
  // pre-classification numbers back (Codex review, PR #289).
  const countsGenRef = useRef(0);

  const syncCounts = useCallback(async (listRows, more) => {
    if (kind === 'all' && !more) {
      countsGenRef.current += 1;   // any reply still in flight is now outdated
      countsKeyRef.current = filterKey;
      setCounts(countRows(listRows));
      return;
    }
    if (countsKeyRef.current === filterKey) return;
    countsKeyRef.current = filterKey;
    const gen = ++countsGenRef.current;
    try {
      const r = await commandCenterApi.getLineItemCounts({
        q: q.length >= MIN_QUERY_CHARS ? q : '', ...supplierFilter, restaurantIds,
      });
      if (gen === countsGenRef.current) setCounts(r.data || null);
    } catch {
      if (gen === countsGenRef.current) { countsKeyRef.current = null; setCounts(null); }
    }
  }, [kind, filterKey, q, supplierFilter, restaurantIds]);

  const load = useCallback(async () => {
    const token = ++loadToken.current;
    setSelected(new Set());
    setError(false);
    // A new or cleared filter makes any counts reply still in flight outdated:
    // advance the generation so it can't land (Codex re-review, PR #289 —
    // נקה סינון brought an old search's numbers back over the empty prompt).
    if (countsKeyRef.current !== filterKey || !ready) {
      countsGenRef.current += 1;
      setCounts(null);   // never show another filter's numbers
    }
    if (!ready) { countsKeyRef.current = null; setRows([]); setHasMore(false); return; }
    setRows(null);
    try {
      const r = await fetchPage(null);
      if (token !== loadToken.current) return;
      const listRows = Array.isArray(r.data?.rows) ? r.data.rows : [];
      const more = Boolean(r.data?.hasMore);
      setRows(listRows);
      setHasMore(more);
      syncCounts(listRows, more);
    } catch {
      if (token !== loadToken.current) return;
      setError(true);
      setRows(null);
    }
  }, [ready, fetchPage, filterKey, syncCounts]);

  // Deliberately no useRefetchOnFocus, unlike the queues: lines change here
  // only through this page's own marks, and a refetch on focus would wipe the
  // admin's ticks (and the "טען עוד" pages) every time they came back from
  // checking a document in the tab openInApp opened.
  useEffect(() => { load(); }, [load]);
  // The LATEST load, for code that awaits something first (classify): calling
  // the `load` captured at click time after a tab switch re-fetched the OLD
  // tab and showed its rows under the new one (Codex review, PR #289).
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);

  const loadMore = async () => {
    const token = loadToken.current;
    setLoadingMore(true);
    try {
      const r = await fetchPage(rows[rows.length - 1].id);
      if (token !== loadToken.current) return;
      setRows((prev) => [...prev, ...(r.data?.rows || [])]);
      setHasMore(Boolean(r.data?.hasMore));
    } catch {
      toast('טעינת שורות נוספות נכשלה', 'error');
    } finally {
      setLoadingMore(false);
    }
  };

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  // Selects the lines on screen only — never everything that matches.
  const toggleAll = () => setSelected((prev) => (
    rows.every((r) => prev.has(r.id)) ? new Set() : new Set(rows.map((r) => r.id))
  ));

  // One click applies: מוצר / שירות directly, לא מוצר through its sub-type
  // menu (owner-approved designer review — no separate "pick, then apply").
  // Sent in batches of CLASSIFY_BATCH: "טען עוד" can put more lines on screen
  // than one request may carry, and select-all used to send them all at once
  // and get a 400 (Codex review, PR #289).
  const classify = async (lineKind, adjustmentType = '') => {
    const ids = [...selected];
    const type = lineKind === LINE_KIND_ADJUSTMENT ? adjustmentType : '';
    setAdjMenuOpen(false);
    setSaving(true);
    let changed = 0;
    let failed = null;
    const moved = {};   // lines per class before the change, summed over batches
    for (let i = 0; i < ids.length && !failed; i += CLASSIFY_BATCH) {
      try {
        const r = await commandCenterApi.classifyLineItems(ids.slice(i, i + CLASSIFY_BATCH), lineKind, type);
        changed += r.data?.changed ?? 0;
        for (const [k, n] of Object.entries(r.data?.moved || {})) moved[k] = (moved[k] || 0) + n;
      } catch (err) {
        failed = err;
      }
    }
    // Patch the summary line from what moved instead of re-counting the whole
    // system after every click (expensive at scale — see ccLineItems.js).
    if (changed) {
      if (summaryStateRef.current === 'ok') setSummary((prev) => patchSummary(prev, moved, lineKind));
      else loadSummary();
    }
    if (failed) {
      const msg = failed?.response?.data?.error || 'הסימון נכשל — אפשר לנסות שוב';
      toast(changed ? `${msg} (${linesCount(changed)} כבר סווגו)` : msg, 'error');
    } else {
      toast(lineKind
        ? `${linesCount(changed)} סווגו כ${lineKindLabel(lineKind, type)}`
        : `הסיווג הוסר מ${linesCount(changed)}`);
    }
    if (changed) countsKeyRef.current = null;   // the marks moved lines between tabs
    try {
      await loadRef.current();
    } finally {
      setSaving(false);
    }
  };

  const hasFilter = Boolean(q || supplierKeys.length || allSuppliers || restaurantIds.length || kind !== 'all');

  // The supplier picker shows a plain selection; a large one is stored and
  // sent as "all but the rest" (see allSuppliers above).
  const groupKeys = useMemo(() => groups.map((g) => g.key), [groups]);
  const pickedSupplierKeys = useMemo(() => {
    if (!allSuppliers) return supplierKeys;
    const out = new Set(excludedKeys);
    return groupKeys.filter((k) => !out.has(k));
  }, [allSuppliers, supplierKeys, excludedKeys, groupKeys]);
  const setSupplierSelection = (keys) => {
    const next = new URLSearchParams(params);
    ['supplier', 'supplier_all', 'supplier_ex'].forEach((k) => next.delete(k));
    if (allSuppliers && keys.length) {
      // Still "all but": what is not ticked is what is left out.
      const picked = new Set(keys);
      const rest = groupKeys.filter((k) => !picked.has(k));
      next.set('supplier_all', '1');
      if (rest.length) next.set('supplier_ex', rest.join(','));
    } else if (keys.length) {
      next.set('supplier', keys.join(','));
    }
    setParams(next, { replace: true });
  };
  const selectAllSuppliers = () => {
    const next = new URLSearchParams(params);
    ['supplier', 'supplier_ex'].forEach((k) => next.delete(k));
    next.set('supplier_all', '1');
    setParams(next, { replace: true });
  };
  const supplierSummary = allSuppliers
    ? (resolvedExcludedKeys.length ? `הכל חוץ מ-${resolvedExcludedKeys.length}` : 'כל הספקים')
    : undefined;
  const clearFilters = () => { setQInput(''); setParams(new URLSearchParams(), { replace: true }); };

  return (
    <>
      {header}
      {summary && <SummaryLine summary={summary} />}

      <div className="cc-filter-wrap">
        <div className="docs-filter-bar">
          <label className={`docs-filter-control docs-filter-searchpill${qInput ? ' is-active' : ''}`}>
            <Icon name="search" size={14} className="docs-filter-microlabel" />
            <input
              className="docs-filter-select"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="שם שורה או מק״ט…"
              aria-label="חיפוש שורה"
              style={{ minWidth: isMobile ? 0 : 180 }}
            />
          </label>
          <MultiSelectFilter
            label="ספק"
            values={pickedSupplierKeys}
            options={groupKeys}
            renderOption={(k) => groupByKey.get(k)?.name || k}
            onChange={setSupplierSelection}
            onSelectAll={selectAllSuppliers}
            summaryText={supplierSummary}
            searchable
            minSearchChars={2}
          />
          <MultiSelectFilter
            label="מסעדה"
            values={restaurantIds}
            options={restaurants.map((r) => String(r.id))}
            renderOption={(id) => restaurantName.get(id) || id}
            onChange={(ids) => updateParam('restaurant', ids.join(','))}
            searchable
            minSearchChars={2}
          />
          {hasFilter && (
            <button className="btn btn-sm btn-secondary" onClick={clearFilters}>
              <Icon name="close" size={12} /> נקה סינון
            </button>
          )}
        </div>
      </div>

      <div className="cc-card">
        <div className="view-tabs" style={{ margin: 0, flexWrap: 'wrap' }}>
          {KINDS.map((k) => (
            <button
              key={k.key}
              className={`view-tab${kind === k.key ? ' is-active' : ''}`}
              onClick={() => updateParam('kind', k.key === 'all' ? '' : k.key)}
            >
              {k.label}
              {tabCount(counts, k.key) != null && <span className="cc-tab-count"> ({tabCount(counts, k.key)})</span>}
            </button>
          ))}
        </div>
      </div>

      {selected.size > 0 && (
        <div className="cc-card cc-bulk-bar" role="region" aria-label="סיווג השורות שנבחרו">
          <strong>נבחרו {linesCount(selected.size)}</strong>
          <div className="cc-kind-seg" role="group" aria-label="סווג כ">
            <span className="cc-muted">סווג כ:</span>
            {['product', 'service'].map((k) => (
              <button key={k} type="button" className="cc-kind-option" disabled={saving} onClick={() => classify(k)}>
                {LINE_KIND_LABELS[k]}
              </button>
            ))}
            <span className="cc-rowmenu cc-kind-menu">
              <button
                type="button"
                className="cc-kind-option"
                aria-haspopup="menu"
                aria-expanded={adjMenuOpen}
                disabled={saving}
                onClick={() => setAdjMenuOpen((o) => !o)}
              >
                {LINE_KIND_LABELS[LINE_KIND_ADJUSTMENT]} <Icon name="chevron" size={12} />
              </button>
              {adjMenuOpen && (
                <>
                  <div className="cc-rowmenu-overlay" onClick={() => setAdjMenuOpen(false)} />
                  <div className="cc-rowmenu-panel" role="menu" aria-label="סוג לא מוצר">
                    <button type="button" role="menuitem" className="cc-rowmenu-item" onClick={() => classify(LINE_KIND_ADJUSTMENT, '')}>
                      ללא סוג
                    </button>
                    {ADJUSTMENT_TYPES.map((t) => (
                      <button key={t} type="button" role="menuitem" className="cc-rowmenu-item" onClick={() => classify(LINE_KIND_ADJUSTMENT, t)}>
                        {ADJUSTMENT_TYPE_LABELS[t]}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </span>
          </div>
          <span className="cc-spacer" />
          <button className="btn btn-sm btn-secondary" disabled={saving} onClick={() => classify('')}>
            הסר סיווג
          </button>
          <button className="btn btn-sm btn-secondary" disabled={saving} onClick={() => setSelected(new Set())}>
            נקה בחירה
          </button>
        </div>
      )}

      <div className="cc-card">
        <LoadState
          loading={rows === null && !error}
          error={error}
          onRetry={load}
          empty={rows?.length === 0}
          emptyMessage={ready ? 'לא נמצאו שורות' : notReadyMessage}
        >
          {rows && (isMobile
            ? <LinesList rows={rows} selected={selected} onToggle={toggle} />
            : <LinesTable rows={rows} selected={selected} onToggle={toggle} onToggleAll={toggleAll} />)}
          {hasMore && (
            <div className="cc-load-more">
              <button className="btn btn-secondary" disabled={loadingMore} onClick={loadMore}>
                {loadingMore ? 'טוען…' : 'טען עוד'}
              </button>
            </div>
          )}
        </LoadState>
      </div>
    </>
  );
}
